import { faCube } from "@fortawesome/pro-regular-svg-icons";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { Outlet, useMatch } from "react-router-dom";

import { Flow } from "src/api/flowTypes";
import { useFlow } from "src/api/queries";
import { LoadingView } from "src/base-components/LoadingView";
import { EmptyState } from "src/design-system/EmptyState";
import { FlowNavSidebar } from "src/flow/FlowNavSidebar";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { useIsSidebarLayout } from "src/hooks/useIsSidebarLayout";
import { DashboardContent } from "src/layout/DashboardContent";
import { LayoutHeader } from "src/layout/Header";
import { PageHeader } from "src/layout/PageHeader";
import { MainSideBar } from "src/mainPage/MainSideBar";
import {
  useFlowContext,
  useWorkspaceContext,
} from "src/router/routerContextHooks";
import {
  FlowPageParams,
  getFlowVersionsUrl,
  getUrlToWsDashboard,
  WsDashboardPageCode,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const HeaderV2: React.FC<{ flow?: Flow }> = ({ flow }) => {
  const { orgId, wsId } = useParamsDecode<FlowPageParams>();

  return (
    <LayoutHeader
      breadcrumbs={[
        {
          key: "flow-name",
          breadcrumb: (
            <LayoutHeader.Breadcrumb
              dataLoc="flow-name"
              href={getFlowVersionsUrl(orgId, wsId, flow?.id ?? "")}
              label={flow?.name ?? ""}
            />
          ),
        },
      ]}
      border
    />
  );
};

export type FlowPageContext = ReturnType<typeof useFlowContext>;

export type FlowPageContextNoData = Partial<FlowPageContext>;

export const FlowPage: React.FC = () => {
  const { flow_id, wsId, orgId } = useParamsDecode<FlowPageParams>();
  const flow = useFlow(flow_id, {
    refetchOnMount: true,
  });
  const { workspace, modules } = useWorkspaceContext();
  const isVersionsPage = useMatch("/org/:orgId/ws/:wsId/flows/:flow_id");
  const isReviewCasePage = useMatch(
    "/org/:orgId/ws/:wsId/flows/:flow_id/review-queue/:case_id",
  );
  const isJobPage = useMatch(
    "/org/:orgId/ws/:wsId/flows/:flow_id/jobs/:job_id",
  );

  const isSideBarEnabled = useIsSidebarLayout();

  // Set dynamic document title based on flow name
  useDocumentTitle(
    flow.data?.name ? [flow.data.name, "Decision Flows"] : ["Decision Flows"],
  );

  if (flow.isError) {
    return (
      <div className="flex w-full items-center justify-center">
        <EmptyState
          actionButton={{
            icon: faArrowLeft,
            text: "Go to Home Page",
            linkTo: getUrlToWsDashboard({
              orgId,
              wsId,
              page: WsDashboardPageCode.Flows,
            }),
          }}
          dataLoc="flow-not-found"
          description="To see all Decision Flows, please go to the Home page."
          headline="This Decision Flow was not found"
          icon={faCube}
        />
      </div>
    );
  }

  const isNoDashboardPage = isReviewCasePage || isJobPage;

  if (isNoDashboardPage) {
    return (
      <LoadingView
        queryResult={flow}
        renderUpdated={(flow: Flow) => (
          <Outlet context={{ flow, workspace, modules, orgId }} />
        )}
      />
    );
  }

  return isSideBarEnabled ? (
    <DashboardContent sidebar={<MainSideBar />} removePadding>
      <PageHeader
        breadcrumbs={[
          {
            key: "flows-page",
            breadcrumb: (
              <PageHeader.Breadcrumb
                dataLoc="flows-page"
                href={getUrlToWsDashboard({
                  orgId,
                  wsId,
                  page: WsDashboardPageCode.Flows,
                })}
                label="Decision Flows"
                hideIcon
              />
            ),
          },
          {
            key: "flow-name",
            breadcrumb: (
              <PageHeader.Breadcrumb
                dataLoc="flow-name"
                href={getFlowVersionsUrl(orgId, wsId, flow?.data?.id ?? "")}
                label={flow?.data?.name ?? ""}
                active
              />
            ),
          },
        ]}
      />
      <DashboardContent sidebar={<FlowNavSidebar flow={flow.data} />}>
        <LoadingView
          queryResult={flow}
          renderUpdated={(flow: Flow) => (
            <Outlet context={{ flow, workspace, modules, orgId }} />
          )}
          renderUpdating={() =>
            isVersionsPage ? (
              <Outlet context={{ workspace, modules, orgId }} />
            ) : (
              <></>
            )
          }
        />
      </DashboardContent>
    </DashboardContent>
  ) : (
    <DashboardContent
      Header={<HeaderV2 flow={flow.data} />}
      sidebar={<FlowNavSidebar flow={flow.data} />}
    >
      <LoadingView
        queryResult={flow}
        renderUpdated={(flow: Flow) => (
          <Outlet context={{ flow, workspace, modules, orgId }} />
        )}
        renderUpdating={() =>
          isVersionsPage ? (
            <Outlet context={{ workspace, modules, orgId }} />
          ) : (
            <></>
          )
        }
      />
    </DashboardContent>
  );
};
